import {
  createContext,
  useContext,
  useState,
  useLayoutEffect,
  useCallback,
  useMemo,
} from "react";
import { translations } from "../data/translations";

const LanguageContext = createContext(null);

function normalizeLang(v) {
  if (v == null || typeof v !== "string") return "en";
  const x = v.trim().toLowerCase();
  return x === "ar" ? "ar" : "en";
}

export function LanguageProvider({ children }) {
  const [lang, setLang] = useState(() =>
    normalizeLang(
      typeof localStorage !== "undefined"
        ? localStorage.getItem("carva-lang")
        : "en",
    ),
  );

  useLayoutEffect(() => {
    const n = normalizeLang(lang);
    document.documentElement.lang = n;
    document.documentElement.dir = n === "ar" ? "rtl" : "ltr";
    try {
      localStorage.setItem("carva-lang", n);
    } catch {
      /* ignore */
    }
  }, [lang]);

  const toggleLang = useCallback(() => {
    setLang((prev) => (normalizeLang(prev) === "en" ? "ar" : "en"));
  }, []);

  const t = useCallback(
    (path, ...args) => {
      const keys = path.split(".");
      let val = translations[normalizeLang(lang)];
      for (const k of keys) {
        if (val == null) return path;
        val = val[k];
      }
      if (typeof val === "function") return val(...args);
      return val ?? path;
    },
    [lang],
  );

  const value = useMemo(
    () => ({
      lang: normalizeLang(lang),
      toggleLang,
      t,
    }),
    [lang, toggleLang, t],
  );

  return (
    <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>
  );
}

export function useLanguage() {
  const ctx = useContext(LanguageContext);
  if (!ctx) {
    throw new Error("useLanguage must be used within LanguageProvider");
  }
  return ctx;
}
